import React, { useEffect, useState } from "react";
import { Power, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminGetSite, adminSetSite, formatApiError } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "—");

// Admin-only emergency Website Pause / Resume (Maintenance Mode). Two-step confirmation; every change is logged.
export default function AdminSitePause() {
  const [site, setSite] = useState(null);
  const [ask, setAsk] = useState(false);
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => adminGetSite().then(setSite).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!site) return null;
  const pausing = !site.paused;
  const word = pausing ? "PAUSE" : "RESUME";
  const open = () => { setReason(""); setTyped(""); setAsk(true); };
  const submit = async () => {
    setBusy(true);
    try {
      setSite(await adminSetSite({ paused: pausing, confirm: typed, reason }));
      toast.success(pausing ? "Website paused" : "Website resumed");
      setAsk(false);
      window.location.reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className={`rounded-2xl border px-4 py-3 mb-5 ${site.paused ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`} data-testid="admin-site-pause">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5"><Power className="w-3.5 h-3.5" /> Website Status</div>
          <div className={`text-sm font-bold ${site.paused ? "text-red-700" : "text-emerald-700"}`} data-testid="admin-site-state">{site.paused ? "Website Paused" : "Website Active"}</div>
          {site.changed_at && <div className="text-xs text-slate-500 mt-0.5" data-testid="admin-site-last-change">Last change {fmt(site.changed_at)} by {site.changed_by}{site.reason ? ` · Reason: ${site.reason}` : ""}</div>}
        </div>
        <button type="button" role="switch" aria-checked={!site.paused} aria-label="Website Active / Paused" onClick={open}
          className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition-colors ${site.paused ? "bg-red-500" : "bg-emerald-600"}`} data-testid="admin-site-toggle">
          <span className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${site.paused ? "translate-x-0.5" : "translate-x-[30px]"}`} />
        </button>
      </div>
      {site.log?.length > 0 && (
        <details className="mt-2 text-xs text-slate-600" data-testid="admin-site-log">
          <summary className="cursor-pointer font-semibold">Pause / Resume log</summary>
          <ul className="mt-1 space-y-0.5">{site.log.map((l) => <li key={`${l.at}-${l.action}`}>{fmt(l.at)} — {l.action === "site_pause" ? "Paused" : "Resumed"} by {l.by}{l.reason ? ` (${l.reason})` : ""}</li>)}</ul>
        </details>
      )}
      <AlertDialog open={ask} onOpenChange={(o) => !busy && setAsk(o)}>
        <AlertDialogContent data-testid="admin-site-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{pausing ? "Pause the whole website?" : "Resume the website?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {pausing
                ? "All visitors, customers and vendors will see the Maintenance Page and cannot browse or save anything until you resume. No data or login is deleted. Only Admin keeps access."
                : "The website will open again for everyone. Existing data and logins continue as before."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pausing && (
            <input value={reason} onChange={(e) => setReason(e.target.value.slice(0, 300))} placeholder="Reason (optional, admin log only)"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="admin-site-reason" />
          )}
          <label className="text-sm text-slate-700">Type <b>{word}</b> to confirm
            <input value={typed} onChange={(e) => setTyped(e.target.value.toUpperCase())} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono" data-testid="admin-site-confirm-input" />
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="admin-site-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); submit(); }} disabled={busy || typed !== word}
              className={pausing ? "bg-red-600 hover:bg-red-700" : ""} data-testid="admin-site-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : pausing ? "Pause website" : "Resume website"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
